import EnumsContext from '../../../../context/enums-context';
import { buildGroupHierarchy, convertSumWithLatestCurrencyRate, findGroupCode, formatCurrency, formatShortDate, getGroupCode, useEnumLabel } from '../../../../Utils/Utils';
import InputTextTemplate from '../../../UIElements/FilterTemplates/InputTextTemplate';
import ProductStatusCircle from '../../../UIElements/ProductStatusCircle';
import { UaLocale } from '../../../../Utils/TranslationUtils';
import useApi from '../../../../Utils/BackendClient';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIsMobile } from '../../../../Utils/ResizeUtils';
import { UserPermissionContext } from '../../../../context/UserPermissionProvider';
import useGetEnums from '../../../../Utils/EnumsUtils';
import { useLocation, useNavigate } from 'react-router-dom';

import { addLocale, locale } from 'primereact/api';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { Column } from 'primereact/column';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { DataTable } from 'primereact/datatable';
import { FloatLabel } from 'primereact/floatlabel';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { SplitButton } from 'primereact/splitbutton';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { Tree } from 'primereact/tree';
import { Sidebar } from 'primereact/sidebar';

import "./AdditionalStyles.css";
import commonStyle from "../../CommonStyles.module.css";
import ProductGroupCreationSidebar from '../../../UIElements/Sidebars/ProductGroupCreationSidebar';

const Products = ({ isDarkTheme }) => {

    const buttonRef = useRef(null);
    const ctx = useContext(EnumsContext);
    const isMobile = useIsMobile();
    const location = useLocation();
    const navigate = useNavigate();
    const permCtx = useContext(UserPermissionContext);
    const toast = useRef(null);

    const { changeProductStatus, deleteProduct, deleteProductGroup, fetchProducts, fetchProductsExtended, fetchProductGroupDetails } = useApi();
    const [lazyState, setlazyState] = useState(location.state?.productPageState !== undefined ? location.state?.productPageState : {
        first: 0,
        rows: 10,
        page: 1,
        sortField: "created_at",
        sortOrder: 1,
        filters: {
            product_name: { value: "", matchMode: 'contains' },
            product_group: { value: null, matchMode: 'equals' },
            product_article: { value: "", matchMode: 'contains' },
            created_at: { value: null, matchMode: 'btw' },
            created_by: { value: null, matchMode: 'equals' }
        }
    });
    const [loading, setLoading] = useState(true);
    const [onlyActive, setOnlyActive] = useState(false)
    const [productCategoriesLoading, setProductCategoriesLoading] = useState(true);
    const [productGroupsLoaded, setProductGroupsLoaded] = useState(false);
    const [productGroupSidebarVisible, setProductGroupSidebarVisible] = useState(false);
    const [products, setProducts] = useState();
    const [reloadData, setReloadData] = useState(true);
    const [reloadProductGroups, setReloadProductGroups] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [showProductGroupMenu, setShowProductGroupMenu] = useState(true);
    const [sortedGroups, setSortedGroups] = useState([]);
    const [tableFilters, setTableFilters] = useState(location.state?.productPageState !== undefined ? location.state?.productPageState?.filters : {
        product_name: { value: "", matchMode: 'contains' },
        product_group: { value: null, matchMode: 'equals', name: '' },
        product_article: { value: "", matchMode: 'contains' },
        created_at: { value: null, matchMode: 'btw' },
        created_by: { value: null, matchMode: 'equals' }
    });
    const [totalRecords, setTotalRecords] = useState(0);
    const [treeHierarchy, setTreeHierarchy] = useState([]);
    const [groupData, setGroupData] = useState(null);
    const [visible, setVisible] = useState(false);

    addLocale('ua', UaLocale);
    locale('ua');

    const enumTypes = useMemo(() => ["product_groups"], []);

    useGetEnums(enumTypes);

    const getProductGroupLabel = useEnumLabel(
        ctx.enums.product_groups.data
    );

    useEffect(() => {
        if (location.state !== null) {
            toast.current.show(location.state.toast);
        }
    }, [location.state]);

    useEffect(() => {

        const fetchData = async () => {

            const response = await fetchProducts(onlyActive, lazyState);

            if (response.status !== 200) {
                toast.current.show({ severity: "error", summary: "Помилка завантаження списку", detail: "Помилка сервера " + response.status, life: 3000 });
                setTotalRecords(0);
                setProducts([]);
            }
            else {
                setTotalRecords(response.data.count);
                setProducts(response.data.results);
            }

            setLoading(false);
        }

        if (productGroupsLoaded) {
            fetchData();
        }

    }, [lazyState, reloadData, fetchProducts, onlyActive, productGroupsLoaded]);

    useEffect(() => {

        if (permCtx.permissions?.includes("products.view_productgroups")) {
            const getProductGroups = async () => {
                const response = await fetchProductsExtended();

                if (response.status === 200) {

                    const groups = buildGroupHierarchy(response.data);

                    const buildTreeHierarchy = (groups) => {
                        return groups.map(group => {
                            const groupBlock = {
                                key: group.id,
                                label: group.group_name,
                                droppable: false,
                                is_active: group.is_active,
                                children: group.sub_groups.length > 0 ? buildTreeHierarchy(group.sub_groups) : undefined,
                            };

                            return groupBlock;
                        });
                    };

                    const hierarchy = buildTreeHierarchy(response.data || []);

                    setTreeHierarchy(hierarchy);
                    setSortedGroups(groups);
                    setProductCategoriesLoading(false);
                }
                else {
                    setSortedGroups([]);
                }

                setProductGroupsLoaded(true);
            }

            getProductGroups();
        }
    }, [fetchProductsExtended, reloadProductGroups, permCtx.permissions]);

    const ChangeProductGroupStatusHandler = (rowData) => {

        const action = rowData.is_active ? "deactivate" : "activate";

        const callApi = async () => {

            const response = await changeProductStatus(rowData.id, action);

            if (response.status !== 200) {
                toast.current.show({ severity: "error", summary: "Помилка зміни статусу", detail: "Помилка сервера " + response.status, life: 3000 });
            }
            else {
                toast.current.show({ severity: 'success', summary: rowData.is_active ? 'Деактивація' : "Активація", detail: rowData.is_active ? 'Товар деактивовано' : "Товар активовано", life: 3000 });
            }

            setReloadData(!reloadData);
        }

        callApi();
    }

    const DeleteProductHandler = async (productIds, isMultiple) => {

        const response = await deleteProduct({ ids: productIds });

        if (response.status !== 204) {
            toast.current.show({ severity: "error", summary: "Помилка видалення", detail: "Помилка сервера " + response.status, life: 3000 });
        }
        else {
            toast.current.show({ severity: "success", summary: 'Видалення', detail: `Товар успішно видалено`, life: 3000 });

            if (isMultiple) { setSelectedProducts(null); }
        }

        setReloadData(!reloadData);
    }

    const EditProductHandler = rowData => {
        const route = '/products/editproduct/' + rowData.id;
        navigate(route, { state: { id: rowData.id, productPageState: lazyState } });
    }

    const onPage = (event) => {
        event.page = event.page + 1;
        event.filters = lazyState.filters;
        event.sortField = lazyState.sortField;
        event.sortOrder = lazyState.sortOrder;
        setlazyState(event);
    };

    const onSort = (event) => {
        event.page = lazyState.page;
        setlazyState(event);
    }

    const onFilter = (event) => {
        event.page = 1;
        setTableFilters(event.filters);
    }

    const OnlyActiveHandler = (event) => {
        setOnlyActive(event);
    }

    const confirmDelete = (event, rowData) => {
        confirmPopup({
            group: 'headless',
            target: event.currentTarget,
            message: 'Підтвердження видалення товару',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'accept',
            acceptLabel: "Так",
            rejectLabel: "Hi",
            accept: () => { DeleteProductHandler([rowData.id], false) }
        });
    }

    const confirmGroupDelete = (event, rowData) => {
        confirmPopup({
            group: 'headless',
            target: event.currentTarget,
            message: 'Підтвердження видалення групи товарів',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'accept',
            acceptLabel: "Так",
            rejectLabel: "Hi",
            accept: () => { handleDelete(rowData) }
        });
    }

    const changeStatus = (event, rowData) => {
        console.log("Here!");
        confirmPopup({
            group: 'headless',
            target: event.currentTarget,
            message: rowData.is_active ? 'Підтвердження деактивації товару' : 'Підтвердження активації товару',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'accept',
            acceptLabel: "Так",
            rejectLabel: "Hi",
            accept: () => { ChangeProductGroupStatusHandler(rowData) }
        });
    }

    const CreateProductHandler = () => {
        navigate('/products/createproduct', { state: { productPageState: lazyState } });
    }

    const formatFilterChips = () => {
        const element = [];

        Object.keys(lazyState.filters).forEach((filterKey) => {
            const filter = lazyState.filters[filterKey];

            if (filter.value && filter.value !== "") {

                let translatedKey = filterKey;
                let changedValue = filter.value;
                let removeValue = null;


                if (filterKey === "product_name") {
                    translatedKey = "Назва товару";
                    removeValue = "";
                }
                else if (filterKey === "product_group") {
                    translatedKey = "Назва групи";
                    changedValue = filter.name;
                }
                else if (filterKey === "product_article") {
                    translatedKey = "Артикул";
                    removeValue = "";
                }
                else if (filterKey === "created_at") {
                    translatedKey = "Дата";
                    changedValue = `з ${formatShortDate(filter.value[0])} до ${formatShortDate(filter.value[1] ?? filter.value[0])}`;
                }

                element.push(
                    <Chip
                        key={filterKey}
                        label={`${translatedKey}: ${changedValue}`}
                        removable
                        onRemove={() => clearFilterFromChips(filterKey, removeValue)} // Optional: To handle removal
                    />
                );
            }
        });

        return <div className="flex flex-wrap gap-2">{element}</div>;
    }

    const filterApply = () => {
        setlazyState(prevState => ({
            ...prevState,
            filters: tableFilters
        }))
    }

    const handleMobileFilterChange = (key, value) => {
        setTableFilters((prevFilters) => ({
            ...prevFilters,
            [key]: { ...prevFilters[key], value: value }
        }));
    }

    const clearMobileFilters = () => {
        setTableFilters({
            product_name: { value: "", matchMode: 'contains' },
            product_group: { value: null, matchMode: 'equals' },
            product_article: { value: "", matchMode: 'contains' },
            created_at: { value: null, matchMode: 'btw' },
            created_by: { value: null, matchMode: 'equals' }
        });

        setlazyState(prevState => ({
            ...prevState,
            filters: {
                product_name: { value: "", matchMode: 'contains' },
                product_group: { value: null, matchMode: 'equals' },
                product_article: { value: "", matchMode: 'contains' },
                created_at: { value: null, matchMode: 'btw' },
                created_by: { value: null, matchMode: 'equals' }
            }
        }));
    }

    const clearFilter = (paramName, value) => {
        setlazyState(prevState => ({
            ...prevState,
            filters: {
                ...prevState.filters,
                [paramName]: {
                    ...prevState.filters[paramName],
                    value: value
                }
            }
        }));
    }

    const clearFilterFromChips = (paramName, value) => {
        clearFilter(paramName, value);
        setTableFilters(prevState => ({
            ...prevState,
            [paramName]: {
                value: value
            }
        }));
    }

    const applyFilterFromMenu = (node) => {
        const filters = tableFilters;
        filters.product_group.value = node.key;
        filters.product_group.name = node.label;
        setTableFilters(filters);

        const state = lazyState;
        state.page = 1;
        state.filters.product_group.value = node.key;
        state.filters.product_group.name = node.label;
        setlazyState(state);

        setProductGroupSidebarVisible(false);

        setReloadData(!reloadData);
    }

    const toolbarRightTemplate = () => {
        return (<div className="flex flex-wrap gap-2">
            {selectedProducts?.length > 0 && <Button label="Видалити" severity="danger" disabled={selectedProducts?.some(obj => obj.can_be_deleted === false)}
                className={selectedProducts?.some(obj => obj.can_be_deleted === false) ? commonStyle.closeButton : commonStyle.deleteButton}
                onClick={() => DeleteProductHandler(selectedProducts.map(item => item.id), true)} />}
            <Button label="Додати товар" severity="success" className={commonStyle.addButton} onClick={CreateProductHandler} />
        </div>)
    }

    const tableHeader = (
        <div className="w-full">
            <div className="flex justify-content-between align-items-center w-full">
                <div>
                    {!isMobile && formatFilterChips()}
                    {isMobile && (<>
                        <Button
                            icon="pi pi-sitemap"
                            severity="secondary"
                            className='mr-2'
                            outlined
                            onClick={() => setProductGroupSidebarVisible(true)}
                        />
                        <Button
                            icon="pi pi-filter-slash"
                            severity="secondary"
                            outlined
                            onClick={() => clearMobileFilters()}
                        />
                    </>
                    )}
                </div>
                <div className="flex align-items-center gap-2">
                    <InputSwitch checked={onlyActive} onChange={(e) => OnlyActiveHandler(e.value)} />
                    <span>Лише активні</span>
                </div>
            </div>
            {showFilters && (
                <div className="grid">
                    <div className="col-12 mt-5">
                        <FloatLabel>
                            <InputText
                                value={tableFilters.product_name.value}
                                onChange={(e) =>
                                    handleMobileFilterChange("product_name", e.target.value)
                                }
                                placeholder="Введіть значення"
                                className="p-column-filter w-full"
                            />
                            <label>Назва товару</label>
                        </FloatLabel>
                    </div>
                    <div className="col-12 mt-3">
                        <FloatLabel>
                            <InputText
                                value={tableFilters.product_article.value}
                                onChange={(e) =>
                                    handleMobileFilterChange("product_article", e.target.value)
                                }
                                placeholder="Введіть значення"
                                className="p-column-filter w-full"
                            />
                            <label>Артикул</label>
                        </FloatLabel>
                    </div>
                    <div className="col-12 flex justify-content-center">
                        <Button
                            severity="success"
                            outlined
                            style={{ width: "100%" }}
                            onClick={() => filterApply()}
                            label="Застосувати"
                        />
                    </div>
                </div>
            )}
        </div>
    );

    const imageBodyTemplate = (product) => {
        return (
            <div className={commonStyle.productImageWrapper}>
                <img
                    src={product.logo}
                    alt={product.product_name}
                    className={commonStyle.productImage}
                />
            </div>
        );
    };

    const buttons = (rowData) => {
        let buttons = [];

        buttons.push({
            label: rowData.is_active ? "Деактивувати" : "Активувати",
            icon: rowData.is_active ? "pi pi-times" : "pi pi-check",
            command: (event) => { changeStatus(event, rowData) }
        });

        if (rowData.can_be_deleted) {
            buttons.push({
                label: 'Видалити',
                icon: 'pi pi-trash',
                command: () => { confirmDelete(rowData) }
            });
        }

        buttons = buttons.length > 0 ? buttons : null;
        return buttons
    };

    const actionBodyTemplate = (rowData) => {
        return (
            !isMobile ? (<div className="flex flex-wrap gap-2 justify-content-center align-items-center">
                {permCtx.permissions?.includes("products.change_products") && <Button
                    icon={rowData.is_active ? "pi pi-times" : "pi pi-check"}
                    className={`p-button-rounded ${rowData.is_active ? commonStyle.warningTag + " p-button-warning" : commonStyle.addButton + " p-button-success"}`}
                    tooltip={rowData.is_active ? "Деактивувати" : "Активувати"}
                    tooltipOptions={{ position: "top" }}
                    onClick={(event) => changeStatus(event, rowData)}
                />}
                {permCtx.permissions?.includes("products.delete_products") && <Button
                    icon="pi pi-trash"
                    className={`p-button-rounded p-button-danger ${rowData.can_be_deleted ? commonStyle.deleteButton : commonStyle.closeButton}`}
                    tooltip={rowData.can_be_deleted ? "Видалити" : "Неможливо видалити"}
                    tooltipOptions={{ showOnDisabled: true, position: "top" }}
                    disabled={!rowData.can_be_deleted}
                    size="small"
                    onClick={(event) => confirmDelete(event, rowData)}
                />}
                {permCtx.permissions?.includes("products.change_products") && <Button
                    icon="pi pi-pencil"
                    className={`p-button-rounded p-button-info ${commonStyle.editButton}`}
                    tooltip="Редагувати"
                    tooltipOptions={{ position: "top" }}
                    onClick={() => EditProductHandler(rowData)}
                />}
            </div>)
                : (
                    <SplitButton
                        label={null}
                        icon="pi pi-pencil"
                        onClick={() => EditProductHandler(rowData)}
                        model={buttons(rowData)}
                        text
                        ref={buttonRef} // Set the ref here
                    />
                )
        );
    };

    const handleGroupAdding = () => {
        setVisible(true);
    }

    const handleEdit = (node) => {

        const getProductGroupsData = async () => {

            const response = await fetchProductGroupDetails(getGroupCode(node.key));

            if (response.status === 200) {

                response.data.root_group = findGroupCode(sortedGroups, response.data.root_group);

                setGroupData(response.data);

                setVisible(true);
            }
        }

        getProductGroupsData();
    };

    const HideSideBar = () => {
        setGroupData(null);
        setVisible(false);
    }

    const ReloadProductCategories = () => {
        setProductCategoriesLoading(true);
        setReloadProductGroups(!reloadProductGroups);

        HideSideBar();
    }

    const handleDelete = (node) => {
        const deleteData = async () => {

            const request = {
                ids: [getGroupCode(node.key)]
            }

            const response = await deleteProductGroup(request);

            if (response.status !== 204) {
                toast.current.show({ severity: "error", summary: "Помилка видалення", detail: "Помилка сервера " + response.status, life: 3000 });
            }
            else {
                setProductCategoriesLoading(true);
                setReloadProductGroups(!reloadProductGroups);
                toast.current.show({ severity: "success", summary: 'Видалення', detail: 'Групу товарів успішно видалено', life: 3000 });
            }

            setReloadData(!reloadData);
        }

        deleteData();
    };

    const nodeTemplate = (node, options) => {
        return (
            <div className="flex justify-content-between align-items-center w-full">
                <div className="flex col-7 items-center">
                    <ProductStatusCircle id={node.id} type="statusCircleSmall" isActive={node.is_active} />
                    <span className="ml-2">{node.label}</span> {/* Optional margin for spacing */}
                </div>
                <div className="col-5 flex justify-content-end align-items-center">
                    {permCtx.permissions?.includes("products.change_productgroups") && <Button
                        icon="pi pi-pencil"
                        severity="secondary"
                        size="small"
                        text
                        tooltip="Редагувати групу"
                        tooltipOptions={{ position: "top" }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(node);
                        }}
                    />}
                    {permCtx.permissions?.includes("products.delete_productgroups") && <Button
                        icon="pi pi-trash"
                        severity="secondary"
                        text
                        tooltip="Видалити групу"
                        tooltipOptions={{ position: "top" }}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            confirmGroupDelete(e, node)
                        }}
                    />}
                </div>
            </div>
        );
    };

    const footer = `Загальна кількість товарів: ${totalRecords != null ? totalRecords : 0}`;

    const productGroupMenuClass = () => {
        if (isMobile) {
            return "col-12";
        }

        if (!permCtx.permissions?.includes("products.view_productgroups")) {
            return "col-12 justify-content-end";
        }

        if (!showProductGroupMenu) {
            return "col-12 flex flex-row";
        }

        return "col-7 md:col-8 xl:col-9 justify-content-end";
    };

    const mobileTableBody = (rowData) => {
        return (
            <div className="flex justify-content-between flex-wrap">
                <div className="grid w-8">
                    <div className="col-2 flex justify-content-center align-items-center">
                        <ProductStatusCircle id={rowData.id} type="statusCircle" isActive={rowData.is_active} />
                    </div>
                    <div className="col-10">
                        <div className="flex-column">
                            <div className='font-bold'>{rowData.product_name}</div>
                            <div>{getProductGroupLabel(rowData.product_group)}</div>
                            <div>Артикул: {rowData.product_article}</div>
                            <div>Ціна: {formatCurrency(rowData.price, rowData.currency)}</div>
                            {rowData.currency !== "UAH" && <div>у грн: {formatCurrency(rowData.priceUAH, "UAH")}</div>}
                        </div>
                    </div>
                </div>
                <div className="flex justify-content-center align-items-center">
                    {actionBodyTemplate(rowData)}
                </div>
            </div>
        )
    }

    const toolbarLeftTemplate = () => {
        return (<>{isMobile && <Button icon="pi pi-filter" severity="secondary" outlined={showFilters ? false : true} onClick={() => setShowFilters(!showFilters)} />}
        </>)
    }

    const productGroupFilter = () => {
        if (isMobile) {
            return (
                <Sidebar visible={productGroupSidebarVisible} onHide={() => setProductGroupSidebarVisible(false)} className="w-full">
                    {productGroupFilterBase()}
                </Sidebar>
            )
        }
        else {
            if (permCtx.permissions?.includes("products.view_productgroups") && showProductGroupMenu) {
                return (<div className={`${showProductGroupMenu ? "col-5 md:col-4 xl:col-3" : "col-1"} ${productCategoriesLoading ? "flex align-items-center justify-content-center" : ""}`} style={{ height: '100vh' }}>
                    {productCategoriesLoading ? (<ProgressSpinner />) : (productGroupFilterBase())}
                </div>);
            }
            else {
                return <></>;
            }
        }
    }

    const productGroupFilterBase = () => {
        return (
            <div className={isDarkTheme ? "darkMode" : "lightMode"}>
                {!isMobile && <div className='flex justify-content-between mr-4 ml-4'>
                    <Button icon="pi pi-step-backward-alt"
                        severity="secondary"
                        outlined
                        tooltip="Приховати групи товарів"
                        tooltipOptions={{ position: "top" }}
                        onClick={() => setShowProductGroupMenu(!showProductGroupMenu)}
                        className='mr-1'
                    />
                    {(permCtx.permissions?.includes("products.add_productgroups") && showProductGroupMenu) && <Button icon="pi pi-plus"
                        severity="success"
                        tooltip="Додати групу"
                        tooltipOptions={{ position: "top" }}
                        onClick={handleGroupAdding}
                        className={`${commonStyle.addButton}`}
                    />}
                </div>}
                <Tree
                    value={treeHierarchy}
                    selectionMode="single"
                    dragdropScope="demo"
                    onDragDrop={(e) => setTreeHierarchy(e.value)}
                    onSelect={e => applyFilterFromMenu(e.node)}
                    className="w-full"
                    filter
                    filterMode="lenient"
                    filterPlaceholder="Пошук групи"
                    nodeTemplate={nodeTemplate}  // Use the nodeTemplate here
                />
            </div>)
    }

    const priceTemplate = (rowData) => {

        const convertedPriceAmount = formatCurrency(rowData.price, rowData.currency);
        if (rowData.sale_price_currency === "UAH") {
            return convertedPriceAmount;
        }
        else {
            return <div className="flex flex-column">
                <div>{convertedPriceAmount}</div>
                <div className="text-sm" style={{ opacity: 0.8 }}>{`(${formatCurrency(convertSumWithLatestCurrencyRate(rowData.currency, rowData.price), "UAH")})`}</div>
            </div>;
        }
    }

    return (<>
        <Toast ref={toast} />
        {permCtx.permissions?.includes("products.add_products") && <Toolbar className={!isMobile && "mb-4"} start={toolbarLeftTemplate} end={toolbarRightTemplate} />}
        <div className='grid'>
            {productGroupFilter()}
            <div className={productGroupMenuClass()}>
                {!showProductGroupMenu && <Button icon={`pi pi-step-forward-alt`}
                    severity="secondary"
                    outlined
                    tooltip={`${showProductGroupMenu ? "Приховати групи товарів" : "Показати групи товарів"}`}
                    tooltipOptions={{ position: "top" }}
                    onClick={() => setShowProductGroupMenu(!showProductGroupMenu)}
                />}
                {isMobile ? (
                    <DataTable value={products} stripedRows dataKey="id" lazy header={tableHeader} loading={loading} paginator onPage={onPage} first={lazyState.first} totalRecords={totalRecords} rows={lazyState.rows} rowsPerPageOptions={[10, 20, 50]}
                        paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown">
                        <Column field="logo" headerStyle={{ display: 'none' }} body={imageBodyTemplate} style={{ width: '10%' }} />
                        <Column field="product_name" headerStyle={{ display: 'none' }} body={mobileTableBody} />
                    </DataTable>
                ) : (
                    permCtx.permissions?.includes("products.view_products") && <DataTable value={products} stripedRows dataKey="id" lazy header={tableHeader} loading={loading} selectionMode="checkbox" selection={selectedProducts} onSelectionChange={(e) => setSelectedProducts(e.value)}
                        paginator onPage={onPage} first={lazyState.first} totalRecords={totalRecords} rows={lazyState.rows} rowsPerPageOptions={[10, 20, 50]}
                        onSort={onSort} sortField={lazyState.sortField} sortOrder={lazyState.sortOrder}
                        emptyMessage="Товарів не знайдено" footer={footer} onFilter={onFilter} filters={tableFilters} style={{ width: '100%' }} tableStyle={{ minWidth: '60rem' }}>
                        <Column selectionMode="multiple" headerStyle={{ width: '3%' }} />
                        <Column field="logo" body={imageBodyTemplate} style={{ width: '5%' }} />
                        <Column field="product_name" header="Назва товару" sortable filter filterElement={InputTextTemplate} onFilterApplyClick={filterApply} onFilterClear={() => clearFilter("product_name", "")} showFilterMatchModes={false} style={{ width: '20%' }} />
                        <Column field="product_group" header="Назва групи" sortable body={(rowData) => getProductGroupLabel(rowData.product_group)} style={{ width: '15%' }} />
                        <Column field="product_article" header="Артикул" sortable filter filterElement={InputTextTemplate} onFilterApplyClick={filterApply} onFilterClear={() => clearFilter("product_article", "")} showFilterMatchModes={false} style={{ width: '15%' }} />
                        <Column field="price" header="Ціна" body={priceTemplate} style={{ width: '12%' }} />
                        <Column field="is_active" header="Статус" align="center" body={rowData => <ProductStatusCircle id={rowData.id} type="statusCircle" isActive={rowData.is_active} />} style={{ width: '5%' }} />
                        {(permCtx.permissions?.includes("products.delete_products") || permCtx.permissions?.includes("products.change_products")) && <Column field="action" body={actionBodyTemplate} style={{ width: '25%' }} />}
                    </DataTable>)}
            </div>
            <ConfirmPopup group="headless" />
            <ProductGroupCreationSidebar visible={visible} onHide={() => HideSideBar()} toast={toast} groups={sortedGroups} onCreate={(id) => ReloadProductCategories(id)} data={groupData} componentKey={location.pathname} />
        </div>
    </>)
};

export default Products;